import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { ScrollListContainerHandle } from "../common/ScrollListContainer";

interface UseScrollAnchorOptions<T> {
  items: T[];
  getItemId: (item: T) => string;
  /**
   * Returns a value that changes when the first item receives new content
   * (e.g. updatedAt timestamp). Used to show GoToTop when the first item
   * gets updated while the user is scrolled down, even if it didn't reorder.
   */
  getItemVersion?: (item: T) => unknown;
  /**
   * ID of the currently selected/open item. When this item moves to the
   * top of the list, scroll compensation and GoToTop button are skipped
   * since the user is already viewing this item's content.
   */
  selectedItemId?: string;
  /**
   * Skip manual scrollTop compensation. Use this for lists with animated
   * entries (e.g. Collapse) where the browser's native overflow-anchor
   * handles scroll stability during animation. Items in these lists should
   * NOT have overflowAnchor: "none" so the browser can anchor to them.
   */
  disableScrollCompensation?: boolean;
}

interface UseScrollAnchorReturn {
  scrollContainerRef: React.RefObject<ScrollListContainerHandle | null>;
  isShowGoToTopButton: boolean;
  goToTop: () => void;
}

// ── Helper functions ──────────────────────────────────────────────────

/**
 * Finds the first visible list item in the scroll container to use as
 * a reference point for measuring displacement after DOM reorders.
 */
function captureAnchor(scrollContainer: HTMLElement) {
  const containerTop = scrollContainer.getBoundingClientRect().top;
  const listItems = scrollContainer.querySelectorAll("li");
  for (let i = 0; i < listItems.length; i++) {
    if (listItems[i].getBoundingClientRect().bottom > containerTop) {
      return {
        element: listItems[i],
        top: listItems[i].getBoundingClientRect().top,
      };
    }
  }
  return null;
}

/**
 * Measures how far the anchor element shifted after a DOM update and
 * adjusts scrollTop to keep the viewport stable.
 *
 * - Positive displacement → items pushed down (item moved from below viewport) → compensate.
 * - Zero displacement → items didn't shift (item moved from above viewport) → nothing to do.
 * - Negative displacement → the anchor itself was the moving item → ignore.
 */
function compensateScrollPosition(
  scrollContainer: HTMLElement,
  anchor: { element: Element; top: number } | null,
) {
  if (!anchor || !anchor.element.isConnected) return;
  const displacement = anchor.element.getBoundingClientRect().top - anchor.top;
  if (displacement > 1) {
    scrollContainer.scrollTop += displacement;
  }
}

/**
 * Checks whether the first item in the list reordered (a different item
 * is now first) while the user was scrolled down.
 */
function didFirstItemReorder(
  firstItemId: string | undefined,
  prevFirstItemId: string | undefined,
  scrollTopBefore: number,
): boolean {
  return (
    prevFirstItemId !== undefined &&
    firstItemId !== prevFirstItemId &&
    scrollTopBefore > 1
  );
}

/**
 * Checks whether the first item stayed in place but received new content
 * (e.g. a new message arrived in the chat already at position 1).
 */
function didFirstItemUpdate(
  firstItemId: string | undefined,
  prevFirstItemId: string | undefined,
  firstItemVersion: unknown,
  prevFirstItemVersion: unknown,
  scrollTopBefore: number,
): boolean {
  return (
    firstItemId === prevFirstItemId &&
    firstItemVersion !== prevFirstItemVersion &&
    prevFirstItemVersion !== undefined &&
    scrollTopBefore > 1
  );
}

// ── Hook ──────────────────────────────────────────────────────────────

/**
 * Hook for scroll stability and GoToTop button in scrollable lists.
 *
 * Features:
 * - Maintains visual scroll position when items reorder
 * - Shows GoToTop button when new items appear at the top while scrolled down
 * - Shows GoToTop button when the first item receives new content (via getItemVersion)
 * - Skips GoToTop for the currently selected/open item
 * - Hides button when user scrolls to top
 */
export function useScrollAnchor<T>({
  items,
  getItemId,
  getItemVersion,
  selectedItemId,
  disableScrollCompensation = false,
}: UseScrollAnchorOptions<T>): UseScrollAnchorReturn {
  const scrollContainerRef = useRef<ScrollListContainerHandle>(null);
  const prevFirstItemIdRef = useRef<string | undefined>(undefined);
  const prevFirstItemVersionRef = useRef<unknown>(undefined);
  const scrollTopBeforeUpdateRef = useRef<number>(0);
  const anchorRef = useRef<{ element: Element; top: number } | null>(null);

  const [isShowGoToTopButton, setIsShowGoToTopButton] = useState(false);

  const firstItemId = items.length > 0 ? getItemId(items[0]) : undefined;
  const firstItemVersion =
    items.length > 0 && getItemVersion ? getItemVersion(items[0]) : undefined;

  // ── Render phase: snapshot scroll state before React commits DOM changes ──
  const scrollContainer = scrollContainerRef.current?.getScrollContainer();
  if (scrollContainer) {
    scrollTopBeforeUpdateRef.current = scrollContainer.scrollTop;
    anchorRef.current =
      !disableScrollCompensation && scrollContainer.scrollTop > 1
        ? captureAnchor(scrollContainer)
        : null;
  }

  // ── Layout effect: runs after DOM update, before browser paint ──
  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current?.getScrollContainer();
    if (!scrollContainer) return;

    const prevFirstItemId = prevFirstItemIdRef.current;
    const scrollTopBefore = scrollTopBeforeUpdateRef.current;

    // Case 1: A different item moved to the top of the list
    if (didFirstItemReorder(firstItemId, prevFirstItemId, scrollTopBefore)) {
      const isSelectedItem = firstItemId === selectedItemId;

      if (!isSelectedItem) {
        if (!disableScrollCompensation) {
          compensateScrollPosition(scrollContainer, anchorRef.current);
        }
        setIsShowGoToTopButton(true);
      }
    }

    // Case 2: Same first item received new content (e.g. new message)
    if (
      getItemVersion &&
      didFirstItemUpdate(
        firstItemId,
        prevFirstItemId,
        firstItemVersion,
        prevFirstItemVersionRef.current,
        scrollTopBefore,
      ) &&
      firstItemId !== selectedItemId
    ) {
      setIsShowGoToTopButton(true);
    }

    prevFirstItemIdRef.current = firstItemId;
    prevFirstItemVersionRef.current = firstItemVersion;
  }, [
    items,
    firstItemId,
    firstItemVersion,
    selectedItemId,
    disableScrollCompensation,
    getItemVersion,
  ]);

  // ── Hide button when user scrolls back to top ──
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current?.getScrollContainer();
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (scrollContainer.scrollTop <= 1) {
        setIsShowGoToTopButton(false);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => scrollContainer.removeEventListener("scroll", handleScroll);
  }, []);

  const goToTop = useCallback(() => {
    const scrollContainer = scrollContainerRef.current?.getScrollContainer();
    if (scrollContainer) {
      scrollContainer.scrollTop = 0;
    }
  }, []);

  return {
    scrollContainerRef,
    isShowGoToTopButton,
    goToTop,
  };
}
